import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, MenuItem, Select, Typography } from '@mui/material';
import { Timeline as ActivityIcon } from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { AppSchemasChartDeploymentComparison } from 'src/api/Api';
import ChartCard from './ChartCard';
import {
  ComparedDeployment,
  DeploymentOption,
  DeploymentPicker,
  SeriesSwatch,
  ViewMode,
  ViewModeToggle,
} from './DeploymentControls';
import {
  MAX_SPECIES_SERIES,
  UI,
  baseLineChartOptions,
  peakSlot,
  speciesSeriesStyle,
  speciesTrendsOf,
  sum,
} from './chartTheme';

const ALL_SPECIES = '__all__';
const DEFAULT_SPECIES_COUNT = 5;

/** Element-wise sum of several count series (for "all species combined"). */
const combine = (series: number[][], length: number): number[] =>
  Array.from({ length }, (_, i) => sum(series.map((s) => s[i] ?? 0)));

export interface SpeciesActivityCardProps {
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  multiDisabledReason?: string;
  primaryId?: string;
  primaryName: string;
  compared: ComparedDeployment[];
  deploymentsList: DeploymentOption[];
  onAddDeployment: (id: string) => void;
  onRemoveDeployment: (id: string) => void;
  comparisons: Map<string, AppSchemasChartDeploymentComparison>;
  timeSlots: string[];
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onUploadClick?: () => void;
}

const selectSx = {
  bgcolor: '#111',
  color: UI.text,
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '6px',
  border: '1px solid #282828',
  height: 32,
  minWidth: 240,
  '& .MuiSelect-select': { py: 0, px: 1.5, display: 'flex', alignItems: 'center', gap: 1 },
  '& .MuiSvgIcon-root': { color: UI.accent },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
};

const menuProps = { PaperProps: { sx: { bgcolor: '#111', border: '1px solid #222', color: '#fff', maxHeight: 340 } } };

const SpeciesActivityCard: React.FC<SpeciesActivityCardProps> = ({
  mode,
  onModeChange,
  multiDisabledReason,
  primaryId,
  primaryName,
  compared,
  deploymentsList,
  onAddDeployment,
  onRemoveDeployment,
  comparisons,
  timeSlots,
  loading,
  error,
  onRetry,
  onUploadClick,
}) => {
  const isMulti = mode === 'multi';

  // ── Single deployment: species at the primary deployment ──────────────────
  const primarySpecies = useMemo(
    () => speciesTrendsOf(primaryId ? comparisons.get(primaryId) : undefined),
    [comparisons, primaryId],
  );

  // Each selected species keeps the colour slot it got when picked
  const [selectedSpecies, setSelectedSpecies] = useState<{ name: string; slot: number }[]>([]);

  useEffect(() => {
    setSelectedSpecies((prev) => {
      const still = prev.filter((p) => primarySpecies.some((s) => s.name === p.name));
      if (still.length > 0) return still;
      return primarySpecies.slice(0, DEFAULT_SPECIES_COUNT).map((s, i) => ({ name: s.name, slot: i }));
    });
  }, [primarySpecies]);

  const toggleSpecies = (name: string) => {
    setSelectedSpecies((prev) => {
      if (prev.some((p) => p.name === name)) return prev.filter((p) => p.name !== name);
      if (prev.length >= MAX_SPECIES_SERIES) return prev;
      const taken = new Set(prev.map((p) => p.slot));
      let slot = 0;
      while (taken.has(slot)) slot += 1;
      return [...prev, { name, slot }];
    });
  };

  // ── Multi deployment: one species (or all) across deployments ─────────────
  const [focusSpecies, setFocusSpecies] = useState<string>(ALL_SPECIES);

  const trendsByDeployment = useMemo(
    () => new Map(compared.map((d) => [d.id, speciesTrendsOf(comparisons.get(d.id))])),
    [compared, comparisons],
  );

  const unionSpecies = useMemo(() => {
    const totals = new Map<string, { total: number; deployments: number }>();
    trendsByDeployment.forEach((trends) => trends.forEach((t) => {
      const entry = totals.get(t.name) ?? { total: 0, deployments: 0 };
      totals.set(t.name, { total: entry.total + t.total, deployments: entry.deployments + 1 });
    }));
    return Array.from(totals, ([name, v]) => ({ name, ...v })).sort((a, b) => b.total - a.total);
  }, [trendsByDeployment]);

  useEffect(() => {
    if (focusSpecies !== ALL_SPECIES && !unionSpecies.some((s) => s.name === focusSpecies)) {
      setFocusSpecies(ALL_SPECIES);
    }
  }, [unionSpecies, focusSpecies]);

  const deploymentSeries = useMemo(() => compared.map((d) => {
    const trends = trendsByDeployment.get(d.id) ?? [];
    const hasData = comparisons.has(d.id) && trends.length > 0;
    const counts = focusSpecies === ALL_SPECIES
      ? combine(trends.map((t) => t.counts), timeSlots.length)
      : trends.find((t) => t.name === focusSpecies)?.counts ?? timeSlots.map(() => 0);
    return { deployment: d, hasData, counts, total: sum(counts), peak: peakSlot(counts, timeSlots) };
  }), [compared, trendsByDeployment, comparisons, focusSpecies, timeSlots]);

  // ── Chart ─────────────────────────────────────────────────────────────────
  const chartOptions: Highcharts.Options = useMemo(() => {
    const series: Highcharts.SeriesOptionsType[] = isMulti
      ? deploymentSeries
        .filter((s) => s.hasData)
        .map((s) => ({
          type: 'line',
          name: s.deployment.name,
          color: s.deployment.color,
          dashStyle: s.deployment.dash,
          data: s.counts,
        }))
      : selectedSpecies
        .map((sel) => {
          const trend = primarySpecies.find((s) => s.name === sel.name);
          const style = speciesSeriesStyle(sel.slot);
          return trend ? {
            type: 'line' as const,
            name: trend.name,
            color: style.color,
            dashStyle: style.dash,
            data: trend.counts,
          } : null;
        })
        .filter((s): s is NonNullable<typeof s> => s !== null);

    return {
      ...baseLineChartOptions({ timeSlots, yTitle: 'Detections', valueSuffix: ' calls' }),
      series,
    };
  }, [isMulti, deploymentSeries, selectedSpecies, primarySpecies, timeSlots]);

  const hasData = isMulti ? deploymentSeries.some((s) => s.hasData) : primarySpecies.length > 0;
  const missing = isMulti ? deploymentSeries.filter((s) => !s.hasData) : [];
  const focusLabel = focusSpecies === ALL_SPECIES ? 'All species combined' : focusSpecies;

  // ── Controls ──────────────────────────────────────────────────────────────
  const singleControls = primarySpecies.length > 0 && (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography sx={{ fontSize: '10px', color: UI.textMuted, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', mr: 0.5 }}>
        Species
      </Typography>
      <Select
        multiple
        size="small"
        value={selectedSpecies.map((s) => s.name)}
        onChange={(e) => {
          const next = e.target.value as string[];
          const changed = next.find((n) => !selectedSpecies.some((s) => s.name === n))
            ?? selectedSpecies.find((s) => !next.includes(s.name))?.name;
          if (changed) toggleSpecies(changed);
        }}
        renderValue={() => `${selectedSpecies.length} of ${primarySpecies.length} species shown`}
        inputProps={{ 'aria-label': 'Species shown on chart' }}
        sx={selectSx}
        MenuProps={menuProps}
      >
        {primarySpecies.map((sp) => {
          const sel = selectedSpecies.find((s) => s.name === sp.name);
          const atLimit = !sel && selectedSpecies.length >= MAX_SPECIES_SERIES;
          return (
            <MenuItem
              key={sp.name}
              value={sp.name}
              disabled={atLimit}
              sx={{ fontSize: '12px', gap: 1 }}
            >
              <Checkbox size="small" checked={Boolean(sel)} sx={{ p: 0.25, color: '#444', '&.Mui-checked': { color: UI.accent } }} />
              <Box sx={{ width: 16, display: 'flex' }}>
                {sel && <SeriesSwatch color={speciesSeriesStyle(sel.slot).color} dash={speciesSeriesStyle(sel.slot).dash} />}
              </Box>
              <Box sx={{ flex: 1 }}>{sp.name}</Box>
              <Box component="span" sx={{ color: UI.textMuted, fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}>
                {sp.total.toLocaleString()}
              </Box>
            </MenuItem>
          );
        })}
      </Select>
      <Button
        size="small"
        onClick={() => setSelectedSpecies(primarySpecies.slice(0, DEFAULT_SPECIES_COUNT).map((s, i) => ({ name: s.name, slot: i })))}
        sx={{ fontSize: '11px', textTransform: 'none', color: UI.textSecondary, '&:hover': { color: UI.accent } }}
      >
        Top {DEFAULT_SPECIES_COUNT}
      </Button>
      <Button
        size="small"
        onClick={() => setSelectedSpecies([])}
        sx={{ fontSize: '11px', textTransform: 'none', color: UI.textSecondary, '&:hover': { color: '#E66767' } }}
      >
        Clear
      </Button>
      <Typography sx={{ fontSize: '11px', color: UI.textMuted, ml: 'auto' }}>
        Up to {MAX_SPECIES_SERIES} species at a time
      </Typography>
    </Box>
  );

  const multiControls = (
    <>
      <DeploymentPicker
        compared={compared}
        deploymentsList={deploymentsList}
        onAdd={onAddDeployment}
        onRemove={onRemoveDeployment}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: '10px', color: UI.textMuted, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', mr: 0.5 }}>
          Species
        </Typography>
        <Select
          size="small"
          value={focusSpecies}
          onChange={(e) => setFocusSpecies(String(e.target.value))}
          inputProps={{ 'aria-label': 'Species to compare' }}
          sx={selectSx}
          MenuProps={menuProps}
        >
          <MenuItem value={ALL_SPECIES} sx={{ fontSize: '12px' }}>All species combined</MenuItem>
          {unionSpecies.map((sp) => (
            <MenuItem key={sp.name} value={sp.name} sx={{ fontSize: '12px', gap: 1 }}>
              <Box sx={{ flex: 1 }}>{sp.name}</Box>
              <Box component="span" sx={{ color: UI.textMuted, fontSize: '11px' }}>
                {sp.deployments}/{compared.length} deployments · {sp.total.toLocaleString()}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Box>
    </>
  );

  // ── Footer: per-deployment readout (direct labels for the overlay) ─────────
  const multiFooter = isMulti && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1 }}>
        {deploymentSeries.filter((s) => s.hasData).map((s) => (
          <Box
            key={s.deployment.id}
            sx={{ p: '10px 12px', borderRadius: '8px', border: `1px solid ${UI.hairline}`, bgcolor: 'rgba(255,255,255,0.015)' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, minWidth: 0 }}>
              <SeriesSwatch color={s.deployment.color} dash={s.deployment.dash} />
              <Typography noWrap sx={{ fontSize: '12px', fontWeight: 700, color: UI.text }} title={s.deployment.name}>
                {s.deployment.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: UI.text, lineHeight: 1.1 }}>
                  {s.total.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: UI.textMuted }}>detections</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: UI.text, lineHeight: 1.1 }}>
                  {s.peak ?? '—'}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: UI.textMuted }}>peak activity</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      {missing.length > 0 && (
        <Typography sx={{ fontSize: '11px', color: UI.textMuted }}>
          No activity data yet for {missing.map((m) => m.deployment.name).join(', ')}.
        </Typography>
      )}
    </Box>
  );

  return (
    <ChartCard
      eyebrow="Bio-Insitu Vocal Dynamics"
      title="Species Activity"
      subtitle={isMulti
        ? <>{focusLabel} · detections per time slot across {compared.length} deployments</>
        : <>Detections per time slot over 24 hours at <b style={{ color: UI.text }}>{primaryName}</b></>}
      actions={<ViewModeToggle value={mode} onChange={onModeChange} multiDisabledReason={multiDisabledReason} />}
      controls={isMulti ? multiControls : singleControls || undefined}
      loading={loading}
      error={error}
      onRetry={onRetry}
      hasData={hasData}
      empty={{
        icon: <ActivityIcon sx={{ fontSize: 36, color: '#444', mb: 0.5 }} />,
        title: 'No species activity recorded yet',
        body: isMulti
          ? 'None of the selected deployments has detected species vocalizations yet. Add another deployment or process more audio.'
          : 'No species vocalizations have been detected at this deployment yet. Process audio files or pick another deployment.',
      }}
      onUploadClick={onUploadClick}
      footer={multiFooter}
    >
      {!isMulti && selectedSpecies.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '12px', color: UI.textMuted }}>Pick at least one species to plot.</Typography>
        </Box>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={chartOptions} immutable />
      )}
    </ChartCard>
  );
};

export default SpeciesActivityCard;
